import React, { useState } from "react";
import {
    Box,
    Divider,
    Grid,
    Tab,
    Tabs,
    Typography,
    TextField,
    Button,
    Autocomplete,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    InputAdornment,
    TablePagination,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from "@mui/material";
import { Search, Delete } from "@mui/icons-material";
import AdminDashboardLayout from "../../../layouts/admin_dashboard_layout/admin_dashboard_layout";
import { collection, addDoc, query, updateDoc, doc, deleteDoc } from "firebase/firestore";
import { useFirestore, useFirestoreCollectionData } from "reactfire";
import { useFeedback } from "../../../providers/feedback/feedback";
import RedonPageSettingsTab from "./redon_page_settings_tab";
import RedonMonthlySlidesTab from "./redon_monthly_slides_tab";

interface Props { }

const bloodGroups = [
    { id: 1, value: "A+" },
    { id: 2, value: "A-" },
    { id: 3, value: "B+" },
    { id: 4, value: "B-" },
    { id: 5, value: "AB+" },
    { id: 6, value: "AB-" },
    { id: 7, value: "O+" },
    { id: 8, value: "O-" },
];

const genderOptions = [
    { id: 1, value: "Male" },
    { id: 2, value: "Female" },
];

const cityOptions = [
    { id: 1, value: "Kundapur" },
    { id: 2, value: "Udupi" },
    { id: 3, value: "Shirva" },
    { id: 4, value: "Karkala" },
    { id: 5, value: "Kallianpur" },
];

function Redon(props: Props) {
    const [currentTab, setCurrentTab] = useState(0);
    const firestore = useFirestore();
    const { showSnackbar } = useFeedback();

    // Form states
    const [fullName, setFullName] = useState("");
    const [city, setCity] = useState<{ id: number; value: string } | null>(null);
    const [mobileNumber, setMobileNumber] = useState("");
    const [mobileError, setMobileError] = useState("");
    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState("");
    const [bloodGroup, setBloodGroup] = useState<{ id: number; value: string } | null>(null);
    const [gender, setGender] = useState<{ id: number; value: string } | null>(null);
    const [lastDonationDate, setLastDonationDate] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [detailSearchTerm, setDetailSearchTerm] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Pagination states
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Selected donor for detail view
    const [selectedDonor, setSelectedDonor] = useState<any>(null);

    // Delete dialog states
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [donorToDelete, setDonorToDelete] = useState<{ id: string; name: string } | null>(null);

    // Fetch donors from Firebase
    const donorsCollection = collection(firestore, "redonDonors");
    const donorsQuery = query(donorsCollection);
    const { data: donors } = useFirestoreCollectionData(donorsQuery);

    const handleChange = (event: React.SyntheticEvent, currentTabVal: number) => {
        setCurrentTab(currentTabVal);
        setSearchTerm(""); // Reset search when changing tabs
        setPage(0); // Reset pagination
    };

    const handleDonorClick = (donor: any) => {
        setSelectedDonor(donor);
        setCurrentTab(2); // Switch to details tab
    };

    const handleSubmit = async () => {
        if (!fullName || !city || !bloodGroup || !gender || !mobileNumber || !email) {
            showSnackbar("Please fill all required fields", "#f44336");
            return;
        }

        if (mobileNumber.length !== 10) {
            setMobileError("Mobile number must be exactly 10 digits");
            return;
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setEmailError("Please enter a valid email address");
            showSnackbar("Invalid email address", "#f44336");
            return;
        }

        // Check for duplicate mobile number
        const duplicateMobile = donors?.find(
            (donor: any) => donor.mobileNumber === mobileNumber
        );
        if (duplicateMobile) {
            setMobileError("This mobile number is already registered");
            showSnackbar("Mobile number already exists", "#f44336");
            return;
        }

        // Check for duplicate email
        const duplicateEmail = donors?.find(
            (donor: any) => donor.email?.toLowerCase() === email.toLowerCase()
        );
        if (duplicateEmail) {
            setEmailError("This email address is already registered");
            showSnackbar("Email address already exists", "#f44336");
            return;
        }

        setIsSubmitting(true);
        try {
            await addDoc(donorsCollection, {
                fullName,
                city,
                mobileNumber,
                email,
                bloodGroup,
                gender,
                lastDonationDate: lastDonationDate || null,
                donationCount: lastDonationDate ? 1 : 0,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
            });

            showSnackbar("Donor added successfully!");
            // Reset form
            setFullName("");
            setCity(null);
            setMobileNumber("");
            setMobileError("");
            setEmail("");
            setEmailError("");
            setBloodGroup(null);
            setGender(null);
            setLastDonationDate("");
        } catch (error) {
            showSnackbar("Error adding donor", "#f44336");
            console.error(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const calculateDaysLeft = (lastDonation: string, gender: string) => {
        if (!lastDonation) return 0;

        const lastDonationDate = new Date(lastDonation);
        const today = new Date();
        const daysPassed = Math.floor((today.getTime() - lastDonationDate.getTime()) / (1000 * 60 * 60 * 24));

        const waitingPeriod = gender === "Male" ? 90 : 120;
        const daysLeft = waitingPeriod - daysPassed;

        return daysLeft > 0 ? daysLeft : 0;
    };

    const handleUpdateDonation = async (donorId: string, gender: string, currentCount: number = 0) => {
        try {
            const donorRef = doc(firestore, "redonDonors", donorId);
            await updateDoc(donorRef, {
                lastDonationDate: new Date().toISOString().split('T')[0],
                donationCount: currentCount + 1,
                updatedAt: new Date().toISOString(),
            });
            showSnackbar("Donation date updated successfully!");
        } catch (error) {
            showSnackbar("Error updating donation date", "#f44336");
            console.error(error);
        }
    };

    const handleDeleteDonor = async (donorId: string, donorName: string) => {
        setDonorToDelete({ id: donorId, name: donorName });
        setDeleteDialogOpen(true);
    };

    const confirmDelete = async () => {
        if (!donorToDelete) return;

        try {
            const donorRef = doc(firestore, "redonDonors", donorToDelete.id);
            await deleteDoc(donorRef);
            showSnackbar("Donor deleted successfully!");
            setDeleteDialogOpen(false);
            setDonorToDelete(null);
        } catch (error) {
            showSnackbar("Error deleting donor", "#f44336");
            console.error(error);
        }
    };

    const cancelDelete = () => {
        setDeleteDialogOpen(false);
        setDonorToDelete(null);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const filteredDonors = donors?.filter((donor: any) => {
        const searchLower = searchTerm.toLowerCase();
        return (
            donor.fullName?.toLowerCase().includes(searchLower) ||
            donor.mobileNumber?.includes(searchTerm) ||
            donor.city?.value?.toLowerCase().includes(searchLower) ||
            donor.bloodGroup?.value?.toLowerCase().includes(searchLower)
        );
    }) || [];

    // Filtered donors for tab 3 detail search
    const detailFilteredDonors = donors?.filter((donor: any) => {
        const searchLower = detailSearchTerm.toLowerCase();
        return (
            donor.fullName?.toLowerCase().includes(searchLower) ||
            donor.mobileNumber?.includes(detailSearchTerm)
        );
    }) || [];

    // Paginated donors for tab 2
    const paginatedDonors = filteredDonors.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    return (
        <AdminDashboardLayout>
            <Box sx={{ p: { xs: 1, md: 2 } }}>
                <Box
                    sx={{
                        width: "100%",
                        borderRadius: 4,
                        my: 0,
                        p: { xs: 2, md: 3 },
                        backgroundColor: '#fff',
                        border: "1px solid rgba(198, 40, 40, 0.1)",
                        boxShadow: "0 20px 48px rgba(138, 25, 25, 0.08)",
                    }}
                >
                    <Box display="flex" flexDirection="row">
                        <Typography variant="h6" fontWeight="600">
                            REDON
                        </Typography>
                    </Box>
                    <Box mt={2} mb={2}>
                        <Divider />
                    </Box>
                    <Box mt={2}>
                        <Grid container spacing={3}>
                            <Grid item md={12} lg={12} sm={12} xs={12}>
                                <Tabs
                                    value={currentTab}
                                    aria-label="redon tabs"
                                    onChange={handleChange}
                                    variant="scrollable"
                                    scrollButtons="auto"
                                    sx={{
                                        mt: 1,
                                        "& .MuiTab-root": {
                                            textTransform: "none",
                                            fontWeight: 700,
                                            minHeight: 56,
                                        },
                                    }}
                                >
                                    <Tab
                                        label="Add Redon Donors"
                                        sx={{
                                            textTransform: "capitalize",
                                            color: "#1B001B",
                                        }}
                                    />
                                    <Tab
                                        label="Donors List"
                                        sx={{
                                            textTransform: "capitalize",
                                            color: "#1B001B",
                                        }}
                                    />
                                    <Tab
                                        label="Donor Details"
                                        sx={{
                                            textTransform: "capitalize",
                                            color: "#1B001B",
                                        }}
                                    />
                                    <Tab
                                        label="Page Content"
                                        sx={{
                                            textTransform: "capitalize",
                                            color: "#1B001B",
                                        }}
                                    />
                                    <Tab
                                        label="Monthly Slides"
                                        sx={{
                                            textTransform: "capitalize",
                                            color: "#1B001B",
                                        }}
                                    />
                                </Tabs>
                            </Grid>

                            {currentTab === 0 && (
                                <Grid item md={12} lg={12} sm={12} xs={12}>
                                    <Box p={3}>
                                        <Typography variant="h6" gutterBottom>
                                            Add Redon Donors
                                        </Typography>
                                        <Grid container spacing={3} mt={2}>
                                            <Grid item md={6} sm={12} xs={12}>
                                                <Typography variant="subtitle2" mb={1}>
                                                    Full Name <span style={{ color: "red" }}>*</span>
                                                </Typography>
                                                <TextField
                                                    fullWidth
                                                    value={fullName}
                                                    onChange={(e) => setFullName(e.target.value)}
                                                    placeholder="Enter full name"
                                                />
                                            </Grid>
                                            <Grid item md={6} sm={12} xs={12}>
                                                <Typography variant="subtitle2" mb={1}>
                                                    City/Place <span style={{ color: "red" }}>*</span>
                                                </Typography>
                                                <Autocomplete
                                                    fullWidth
                                                    value={city}
                                                    onChange={(e, value) => setCity(value)}
                                                    options={cityOptions}
                                                    getOptionLabel={(option) => option.value}
                                                    renderInput={(params) => (
                                                        <TextField {...params} placeholder="Select city or place" />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item md={6} sm={12} xs={12}>
                                                <Typography variant="subtitle2" mb={1}>
                                                    Mobile Number <span style={{ color: "red" }}>*</span>
                                                </Typography>
                                                <TextField
                                                    fullWidth
                                                    value={mobileNumber}
                                                    onChange={(e) => {
                                                        const value = e.target.value.replace(/\D/g, "");
                                                        if (value.length <= 10) {
                                                            setMobileNumber(value);
                                                            setMobileError("");
                                                        }
                                                    }}
                                                    placeholder="Enter 10-digit mobile number"
                                                    error={!!mobileError}
                                                    helperText={mobileError}
                                                    inputProps={{
                                                        inputMode: "numeric",
                                                        pattern: "[0-9]*",
                                                        maxLength: 10,
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item md={6} sm={12} xs={12}>
                                                <Typography variant="subtitle2" mb={1}>
                                                    Email <span style={{ color: "red" }}>*</span>
                                                </Typography>
                                                <TextField
                                                    fullWidth
                                                    type="email"
                                                    value={email}
                                                    onChange={(e) => {
                                                        setEmail(e.target.value);
                                                        setEmailError("");
                                                    }}
                                                    placeholder="Enter email address"
                                                    error={!!emailError}
                                                    helperText={emailError}
                                                />
                                            </Grid>
                                            <Grid item md={6} sm={12} xs={12}>
                                                <Typography variant="subtitle2" mb={1}>
                                                    Blood Group <span style={{ color: "red" }}>*</span>
                                                </Typography>
                                                <Autocomplete
                                                    fullWidth
                                                    value={bloodGroup}
                                                    onChange={(e, value) => setBloodGroup(value)}
                                                    options={bloodGroups}
                                                    getOptionLabel={(option) => option.value}
                                                    renderInput={(params) => (
                                                        <TextField {...params} placeholder="Select blood group" />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item md={6} sm={12} xs={12}>
                                                <Typography variant="subtitle2" mb={1}>
                                                    Gender <span style={{ color: "red" }}>*</span>
                                                </Typography>
                                                <Autocomplete
                                                    fullWidth
                                                    value={gender}
                                                    onChange={(e, value) => setGender(value)}
                                                    options={genderOptions}
                                                    getOptionLabel={(option) => option.value}
                                                    renderInput={(params) => (
                                                        <TextField {...params} placeholder="Select gender" />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item md={6} sm={12} xs={12}>
                                                <Typography variant="subtitle2" mb={1}>
                                                    Last Blood Donation Date (Optional)
                                                </Typography>
                                                <TextField
                                                    fullWidth
                                                    type="date"
                                                    value={lastDonationDate}
                                                    onChange={(e) => setLastDonationDate(e.target.value)}
                                                    InputLabelProps={{
                                                        shrink: true,
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item md={12} sm={12} xs={12}>
                                                <Button
                                                    variant="contained"
                                                    onClick={handleSubmit}
                                                    disabled={isSubmitting || !fullName || !city || !mobileNumber || mobileNumber.length !== 10 || !bloodGroup || !gender}
                                                    sx={{ minWidth: 200 }}
                                                >
                                                    {isSubmitting ? "Adding..." : "Add Donor"}
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Grid>
                            )}

                            {currentTab === 1 && (
                                <Grid item md={12} lg={12} sm={12} xs={12}>
                                    <Box p={3}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                            <Typography variant="h6">
                                                Donors List
                                            </Typography>
                                            <TextField
                                                size="small"
                                                placeholder="Search donors..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Search />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                sx={{ width: 300 }}
                                            />
                                        </Box>
                                        <TableContainer component={Paper}>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell><strong>Sl. No.</strong></TableCell>
                                                        <TableCell><strong>Full Name</strong></TableCell>
                                                        <TableCell><strong>Mobile Number</strong></TableCell>
                                                        <TableCell><strong>Email</strong></TableCell>
                                                        <TableCell><strong>Blood Group</strong></TableCell>
                                                        <TableCell><strong>Last Donation</strong></TableCell>
                                                        <TableCell><strong>Status</strong></TableCell>
                                                        <TableCell><strong>Action</strong></TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {paginatedDonors.length > 0 ? (
                                                        paginatedDonors.map((donor: any, index: number) => {
                                                            const daysLeft = calculateDaysLeft(
                                                                donor.lastDonationDate,
                                                                donor.gender?.value
                                                            );
                                                            return (
                                                                <TableRow
                                                                    key={donor.NO_ID_FIELD}
                                                                    hover
                                                                    sx={{
                                                                        cursor: 'pointer',
                                                                        '&:hover': { backgroundColor: '#f5f5f5' }
                                                                    }}
                                                                >
                                                                    <TableCell onClick={() => handleDonorClick(donor)}>{page * rowsPerPage + index + 1}</TableCell>
                                                                    <TableCell onClick={() => handleDonorClick(donor)}>{donor.fullName}</TableCell>
                                                                    <TableCell onClick={() => handleDonorClick(donor)}>{donor.mobileNumber}</TableCell>
                                                                    <TableCell onClick={() => handleDonorClick(donor)}>{donor.email || "N/A"}</TableCell>
                                                                    <TableCell onClick={() => handleDonorClick(donor)}>{donor.bloodGroup?.value}</TableCell>
                                                                    <TableCell onClick={() => handleDonorClick(donor)}>
                                                                        {donor.lastDonationDate || "N/A"}
                                                                    </TableCell>
                                                                    <TableCell onClick={() => handleDonorClick(donor)}>
                                                                        {daysLeft === 0 ? (
                                                                            <Chip
                                                                                label="Available"
                                                                                color="success"
                                                                                size="small"
                                                                            />
                                                                        ) : (
                                                                            <Chip
                                                                                label={`${daysLeft} days left`}
                                                                                color="warning"
                                                                                size="small"
                                                                            />
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <Box display="flex" gap={1}>
                                                                            <Button
                                                                                variant="contained"
                                                                                size="small"
                                                                                disabled={daysLeft !== 0}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleUpdateDonation(donor.NO_ID_FIELD, donor.gender?.value, donor.donationCount || 0);
                                                                                }}
                                                                            >
                                                                                Donated Today
                                                                            </Button>
                                                                            <IconButton
                                                                                color="error"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleDeleteDonor(donor.NO_ID_FIELD, donor.fullName);
                                                                                }}
                                                                                size="small"
                                                                            >
                                                                                <Delete />
                                                                            </IconButton>
                                                                        </Box>
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })
                                                    ) : (
                                                        <TableRow>
                                                            <TableCell colSpan={7} align="center">
                                                                {searchTerm ? "No donors found" : "No donors added yet"}
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        <TablePagination
                                            rowsPerPageOptions={[5, 10, 25, 50]}
                                            component="div"
                                            count={filteredDonors.length}
                                            rowsPerPage={rowsPerPage}
                                            page={page}
                                            onPageChange={handleChangePage}
                                            onRowsPerPageChange={handleChangeRowsPerPage}
                                        />
                                    </Box>
                                </Grid>
                            )}

                            {currentTab === 2 && (
                                <Grid item md={12} lg={12} sm={12} xs={12}>
                                    <Box p={3}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                            <Typography variant="h6" fontWeight={600}>
                                                Donor Details
                                            </Typography>
                                            <TextField
                                                size="small"
                                                placeholder="Search by name or mobile..."
                                                value={detailSearchTerm}
                                                onChange={(e) => setDetailSearchTerm(e.target.value)}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <Search />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                sx={{ width: 300 }}
                                            />
                                        </Box>
                                        {selectedDonor ? (
                                            <>
                                                <Box display="flex" alignItems="center" justifyContent="space-between" mb={4}>
                                                    <Typography variant="h6" fontWeight={600}>
                                                        {selectedDonor.fullName}
                                                    </Typography>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={() => {
                                                            setSelectedDonor(null);
                                                            setCurrentTab(1);
                                                        }}
                                                    >
                                                        Back to List
                                                    </Button>
                                                </Box>
                                                <Box
                                                    sx={{
                                                        p: 3,
                                                        backgroundColor: '#f8f9fa',
                                                        borderRadius: 2,
                                                        border: '1px solid #e0e0e0'
                                                    }}
                                                >
                                                    <Grid container spacing={3}>
                                                        <Grid item md={6} sm={12} xs={12}>
                                                            <Typography variant="subtitle2" color="primary" fontWeight={600}>
                                                                Full Name
                                                            </Typography>
                                                            <Typography variant="body1" mt={1}>
                                                                {selectedDonor.fullName || "-"}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item md={6} sm={12} xs={12}>
                                                            <Typography variant="subtitle2" color="primary" fontWeight={600}>
                                                                Mobile Number
                                                            </Typography>
                                                            <Typography variant="body1" mt={1}>
                                                                {selectedDonor.mobileNumber || "-"}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item md={6} sm={12} xs={12}>
                                                            <Typography variant="subtitle2" color="primary" fontWeight={600}>
                                                                Email
                                                            </Typography>
                                                            <Typography variant="body1" mt={1}>
                                                                {selectedDonor.email || "-"}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item md={6} sm={12} xs={12}>
                                                            <Typography variant="subtitle2" color="primary" fontWeight={600}>
                                                                City/Place
                                                            </Typography>
                                                            <Typography variant="body1" mt={1}>
                                                                {selectedDonor.city?.value || "-"}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item md={6} sm={12} xs={12}>
                                                            <Typography variant="subtitle2" color="primary" fontWeight={600}>
                                                                Blood Group
                                                            </Typography>
                                                            <Typography variant="body1" mt={1}>
                                                                {selectedDonor.bloodGroup?.value || "-"}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item md={6} sm={12} xs={12}>
                                                            <Typography variant="subtitle2" color="primary" fontWeight={600}>
                                                                Gender
                                                            </Typography>
                                                            <Typography variant="body1" mt={1}>
                                                                {selectedDonor.gender?.value || "-"}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item md={6} sm={12} xs={12}>
                                                            <Typography variant="subtitle2" color="primary" fontWeight={600}>
                                                                Last Blood Donation Date
                                                            </Typography>
                                                            <Typography variant="body1" mt={1}>
                                                                {selectedDonor.lastDonationDate || "N/A"}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item md={6} sm={12} xs={12}>
                                                            <Typography variant="subtitle2" color="primary" fontWeight={600}>
                                                                Donation Status
                                                            </Typography>
                                                            <Box mt={1}>
                                                                {calculateDaysLeft(selectedDonor.lastDonationDate, selectedDonor.gender?.value) === 0 ? (
                                                                    <Chip
                                                                        label="Available to Donate"
                                                                        color="success"
                                                                        size="medium"
                                                                    />
                                                                ) : (
                                                                    <Chip
                                                                        label={`${calculateDaysLeft(selectedDonor.lastDonationDate, selectedDonor.gender?.value)} days left`}
                                                                        color="warning"
                                                                        size="medium"
                                                                    />
                                                                )}
                                                            </Box>
                                                        </Grid>
                                                        <Grid item md={6} sm={12} xs={12}>
                                                            <Typography variant="subtitle2" color="primary" fontWeight={600}>
                                                                Number of Times Blood Donated
                                                            </Typography>
                                                            <Typography variant="body1" mt={1} fontWeight={600} color="success.main">
                                                                {selectedDonor.donationCount || 0} {selectedDonor.donationCount === 1 ? 'time' : 'times'}
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item md={6} sm={12} xs={12}>
                                                            <Typography variant="subtitle2" color="primary" fontWeight={600}>
                                                                Created Date
                                                            </Typography>
                                                            <Typography variant="body1" mt={1}>
                                                                {selectedDonor.createdAt ? new Date(selectedDonor.createdAt).toLocaleDateString() : "-"}
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </>
                                        ) : (
                                            <>
                                                {detailSearchTerm && detailFilteredDonors.length > 0 ? (
                                                    <Box>
                                                        <Typography variant="subtitle1" mb={2} fontWeight={600}>
                                                            Search Results ({detailFilteredDonors.length})
                                                        </Typography>
                                                        <Grid container spacing={2}>
                                                            {detailFilteredDonors.map((donor: any) => (
                                                                <Grid item md={12} key={donor.NO_ID_FIELD}>
                                                                    <Box
                                                                        onClick={() => setSelectedDonor(donor)}
                                                                        sx={{
                                                                            p: 2,
                                                                            backgroundColor: '#fff',
                                                                            borderRadius: 1,
                                                                            border: '1px solid #e0e0e0',
                                                                            cursor: 'pointer',
                                                                            '&:hover': {
                                                                                backgroundColor: '#f5f5f5',
                                                                                borderColor: '#1976d2'
                                                                            }
                                                                        }}
                                                                    >
                                                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                                                            <Box>
                                                                                <Typography variant="h6" fontWeight={600}>
                                                                                    {donor.fullName}
                                                                                </Typography>
                                                                                <Typography variant="body2" color="text.secondary">
                                                                                    Mobile: {donor.mobileNumber} | Blood Group: {donor.bloodGroup?.value}
                                                                                </Typography>
                                                                            </Box>
                                                                            <Chip
                                                                                label={calculateDaysLeft(donor.lastDonationDate, donor.gender?.value) === 0 ? "Available" : `${calculateDaysLeft(donor.lastDonationDate, donor.gender?.value)} days left`}
                                                                                color={calculateDaysLeft(donor.lastDonationDate, donor.gender?.value) === 0 ? "success" : "warning"}
                                                                                size="small"
                                                                            />
                                                                        </Box>
                                                                    </Box>
                                                                </Grid>
                                                            ))}
                                                        </Grid>
                                                    </Box>
                                                ) : detailSearchTerm ? (
                                                    <Box textAlign="center" py={5}>
                                                        <Typography variant="h6" color="text.secondary">
                                                            No donors found
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" mt={2}>
                                                            Try searching with a different name or mobile number
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Box textAlign="center" py={5}>
                                                        <Typography variant="h6" color="text.secondary">
                                                            No donor selected
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" mt={2}>
                                                            Search for a donor by name or mobile number, or click on a donor from the Donors List tab
                                                        </Typography>
                                                        <Button
                                                            variant="contained"
                                                            onClick={() => setCurrentTab(1)}
                                                            sx={{ mt: 3 }}
                                                        >
                                                            Go to Donors List
                                                        </Button>
                                                    </Box>
                                                )}
                                            </>
                                        )}
                                    </Box>
                                </Grid>
                            )}

                            {currentTab === 3 && (
                                <Grid item md={12} lg={12} sm={12} xs={12}>
                                    <RedonPageSettingsTab />
                                </Grid>
                            )}

                            {currentTab === 4 && (
                                <Grid item md={12} lg={12} sm={12} xs={12}>
                                    <RedonMonthlySlidesTab />
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                </Box>

                {/* Delete Confirmation Dialog */}
                <Dialog
                    open={deleteDialogOpen}
                    onClose={cancelDelete}
                    aria-labelledby="delete-dialog-title"
                    aria-describedby="delete-dialog-description"
                >
                    <DialogTitle id="delete-dialog-title">
                        Confirm Delete
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText id="delete-dialog-description">
                            Are you sure you want to delete <strong>{donorToDelete?.name}</strong>?
                            This action cannot be undone.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={cancelDelete} color="primary">
                            Cancel
                        </Button>
                        <Button onClick={confirmDelete} color="error" variant="contained" autoFocus>
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </AdminDashboardLayout>
    );
}

export default Redon;
